import type { ReactNode } from "react";

import type { PerfRow } from "../types";

import { DocumentIcon, ExpandIcon, GlobeIcon, LinkIcon } from "../../Icons";
import { Typo } from "../../Typo";
import { BannerThumb } from "../components/BannerThumb";
import { CountryFlag } from "../components/CountryFlag";
import { rowLabel } from "../helpers";
import { usePerformanceReportTranslations } from "../hooks/usePerformanceReportTranslations";

const MetaItem = ({ children }: { children: ReactNode }) => (
  <span className="inline-flex shrink-0 items-center gap-1">{children}</span>
);

export const SourceCell = ({
  row,
  isMobile = false,
}: {
  row: PerfRow;
  isMobile?: boolean;
}) => {
  const { countryUnknown, noCampaign } = usePerformanceReportTranslations();
  const name = rowLabel(row.name, row, countryUnknown, noCampaign);

  if (isMobile) {
    const icon =
      row.type === "banner" ? (
        <BannerThumb row={row} />
      ) : row.type === "country" ? (
        <CountryFlag code={row.code} />
      ) : row.type === "link" ? (
        <div className="flex h-10 w-12 shrink-0 items-center justify-center rounded-lg bg-surface-brand-subtle text-icon-brand-default">
          <LinkIcon
            className="h-4 w-4 [&_path]:stroke-icon-brand-default [&_path]:stroke-1 [&_path]:[vector-effect:non-scaling-stroke]"
            aria-hidden="true"
            focusable="false"
          />
        </div>
      ) : null;

    return (
      <div className="flex w-full min-w-0 items-center gap-3 overflow-hidden">
        {icon}
        <Typo
          element="span"
          type="bodySM"
          textColor="text-text-primary"
          className="min-w-0 flex-1"
        >
          {name}
        </Typo>
      </div>
    );
  }

  if (row.type === "banner") {
    // min-h matches the link two-line title+meta stack so row height stays
    // consistent across link and banner rows in the Source tab.
    return (
      <div className="flex min-h-[49px] w-full min-w-0 items-center gap-3 overflow-hidden">
        <BannerThumb row={row} />
        <div className="flex min-w-0 flex-1 flex-col gap-1 overflow-hidden">
          <Typo
            element="span"
            type="subheadingSM"
            textColor="text-text-primary"
            className="block truncate"
          >
            {name}
          </Typo>
          {(row.dim || row.lang) && (
            <div className="flex min-w-0 items-center gap-1.5 text-text-secondary">
              {row.dim && (
                <span className="inline-flex min-w-[32px] shrink items-center gap-1 overflow-hidden">
                  <ExpandIcon
                    className="h-3.5 w-3.5 shrink-0"
                    aria-hidden="true"
                    focusable="false"
                  />
                  <Typo
                    element="span"
                    type="captionXS"
                    textColor="text-text-secondary"
                    className="truncate"
                  >
                    {row.dim}
                  </Typo>
                </span>
              )}
              {row.dim && row.lang && (
                <span className="shrink-0 text-text-disabled">·</span>
              )}
              {row.lang && (
                <MetaItem>
                  <GlobeIcon
                    className="h-3.5 w-3.5"
                    aria-hidden="true"
                    focusable="false"
                  />{" "}
                  <Typo
                    element="span"
                    type="captionXS"
                    textColor="text-text-secondary"
                  >
                    {row.lang}
                  </Typo>
                </MetaItem>
              )}
            </div>
          )}
        </div>
      </div>
    );
  }

  if (row.type === "country") {
    // min-h matches the banner/link two-line title+meta stack so row height stays
    // consistent across the Source, Country, and Rebate program tabs — including
    return (
      <div className="flex min-h-[49px] w-full min-w-0 items-center gap-3 overflow-hidden">
        <CountryFlag code={row.code} />
        <div className="flex min-w-0 flex-1 flex-col gap-1 overflow-hidden">
          <Typo
            element="span"
            type="subheadingSM"
            textColor="text-text-primary"
            className="block truncate"
          >
            {name}
          </Typo>
        </div>
      </div>
    );
  }

  // Campaign rows are an aggregate over links and banners, so they carry no
  // creative or landing page of their own — a plain name, like `plan`. Without
  // this branch they would fall through to the link renderer below and pick up
  // a link icon.
  if (row.type === "plan" || row.type === "campaign") {
    return (
      <div className="flex min-h-[49px] w-full min-w-0 items-center overflow-hidden">
        <Typo
          element="span"
          type="subheadingSM"
          textColor="text-text-primary"
          className="block truncate"
        >
          {name}
        </Typo>
      </div>
    );
  }

  // link
  return (
    <div className="flex w-full min-w-0 items-center gap-3 overflow-hidden">
      <div className="flex h-10 w-12 shrink-0 items-center justify-center rounded-lg bg-surface-brand-subtle text-icon-brand-default">
        <LinkIcon
          className="h-4 w-4 [&_path]:stroke-icon-brand-default [&_path]:stroke-1 [&_path]:[vector-effect:non-scaling-stroke]"
          aria-hidden="true"
          focusable="false"
        />
      </div>
      <div className="flex min-w-0 flex-1 flex-col gap-1 overflow-hidden">
        <div className="flex min-w-0 items-center gap-2">
          <Typo
            element="span"
            type="subheadingSM"
            textColor="text-text-primary"
            className="block truncate"
          >
            {row.name}
          </Typo>
        </div>
        <div className="flex min-w-0 items-center gap-1.5 text-text-secondary">
          <span className="inline-flex min-w-[32px] shrink items-center gap-1 overflow-hidden">
            <DocumentIcon
              className="h-3.5 w-3.5 shrink-0"
              aria-hidden="true"
              focusable="false"
            />
            <Typo
              element="span"
              type="captionXS"
              textColor="text-text-secondary"
              className="truncate"
            >
              {row.page}
            </Typo>
          </span>
          <span className="shrink-0 text-text-disabled">·</span>
          <MetaItem>
            <GlobeIcon
              className="h-3.5 w-3.5"
              aria-hidden="true"
              focusable="false"
            />{" "}
            <Typo
              element="span"
              type="captionXS"
              textColor="text-text-secondary"
            >
              {row.lang}
            </Typo>
          </MetaItem>
        </div>
      </div>
    </div>
  );
};
